
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  useTheme,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useGlobalConfig } from 'context/GlobalContext';
import { BglPrimaryButton } from 'components/button/BglButtons';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import ViewStreamOutlinedIcon from '@mui/icons-material/ViewStreamOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { PilotApis } from 'apis/pilotAPI';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';

interface AddNewUserComponentProps {
  onBack?: () => void;
}

export const AddNewUserComponent = ({ onBack }: AddNewUserComponentProps) => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [uuids, setUuids] = useState('');
  const [stagedUuids, setStagedUuids] = useState<string[]>([]);
  const [selectedMeterTypes, setSelectedMeterTypes] = useState<string>('');
  const [meterFuelTypes, setMeterFuelTypes] = useState<string[]>([]);
  const [loadingMeterTypes, setLoadingMeterTypes] = useState<boolean>(false);
  const [meterTypesError, setMeterTypesError] = useState<string>('');

  const handleBackToManageUsers = () => {
    if (onBack) {
      onBack();
    } else {
      rc.uiRouter.navigate(ROUTE_TREE.PROJECT.CUSTOMER_DATA_GENERATOR);
    }
  };

  const getEnvironmentConfig = (envId: string) => {
    return environments.find(env => env.id === envId);
  };

  const handleNext = async () => {
    const envConfig = getEnvironmentConfig(selectedEnvironment);
    console.log('Selected Environment Config:', envConfig);
    console.log('Staged UUIDs:', stagedUuids);
    console.log('Selected Meter Types:', selectedMeterTypes);
    
    if (!envConfig) {
      console.error('Environment configuration not found');
      return;
    }

    // Save user ingestion data to localStorage
    const userIngestionData = {
      selectedEnvironment,
      stagedUuids,
      selectedMeterTypes,
      environmentConfig: envConfig,
      timestamp: Date.now() 
    };
    
    try {
      localStorage.setItem('userIngestionData', JSON.stringify(userIngestionData));
      console.log('Saved user ingestion data to localStorage:', userIngestionData);
      
      // Navigate to update metadata page
      rc.uiRouter.navigate(ROUTE_TREE.PROJECT.UPDATE_METADATA);
    } catch (error) {
      console.error('Failed to save data to localStorage:', error);
      // Fallback: show error message
      alert('Failed to save data. Please try again.');
    }
  };

  const handleUuidInput = (value: string) => {
    setUuids(value);
  };

  const handleUuidKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      stageUuids();
    }
  };

  const stageUuids = () => {
    if (!uuids.trim()) return;
    
    const newUuids = uuids
      .split(/[,\n]/)
      .map(uuid => uuid.trim())
      .filter(uuid => uuid && !stagedUuids.includes(uuid));
    
    if (newUuids.length > 0) {
      setStagedUuids(prev => [...prev, ...newUuids]);
      setUuids('');
    }
  };

  const removeStagedUuid = (uuidToRemove: string) => {
    setStagedUuids(prev => prev.filter(uuid => uuid !== uuidToRemove));
  };

  const handleMeterTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedMeterTypes(event.target.value);
  };

  // Function to fetch meter fuel types from ingestion config
  const fetchMeterFuelTypes = async () => {
    if (!selectedPilotId) {
      console.warn('No pilot selected for fetching meter fuel types');
      return;
    }

    try {
      setLoadingMeterTypes(true);
      setMeterTypesError('');
      const response = await PilotApis.getIngestionConfigs(rc, selectedPilotId.toString());
      
      if (response.success && response.data) {
        let supportedMeterTypes: string[] = [];
        let supportedFuels: string[] = [];
        
        const meterTypesConfig = response.data.find(config => 
          config.configKey === 'supported_meter_types'
        );
        console.log('Meter types config found:', meterTypesConfig);
        // Find allowed_measurement_type config
        const measurementTypesConfig = response.data.find(config => 
          config.configKey === 'allowed_measurement_type'
        );
        
        if (!meterTypesConfig) {
          setMeterTypesError('Meter types configuration not found. Please check pilot configuration.');
          setMeterFuelTypes([]);
          return;
        }
        
        if (!measurementTypesConfig) {
          setMeterTypesError('Measurement types configuration not found. Please check pilot configuration.');
          setMeterFuelTypes([]);
          return;
        }
        
        // Parse supported meter types from defaultValue (JSON string)
        if (meterTypesConfig?.defaultValue) {
          try {
            let rawValue = meterTypesConfig.defaultValue;
            
            // Handle "N/A" or invalid values
            if (rawValue === 'N/A' || rawValue === 'null' || rawValue === 'undefined') {
              setMeterTypesError('Meter types configuration contains invalid data. Please check pilot configuration <supported_meter_types>.');
              setMeterFuelTypes([]);
              return;
            }
            
            if (rawValue.includes('\\\\"')) {
              rawValue = rawValue.replace(/\\\\"/g, '\\"');
              console.log('After handling double-escaped quotes:', rawValue);
            } else if (rawValue.includes('\\"')) {
              rawValue = rawValue.replace(/\\"/g, '"');
              console.log('After handling single-escaped quotes:', rawValue);
            }
            
            const parsedMeterTypes = JSON.parse(rawValue);
            console.log('Parsed meter types:', parsedMeterTypes);
            
            if (Array.isArray(parsedMeterTypes)) {
              supportedMeterTypes = parsedMeterTypes;
            } else {
              setMeterTypesError('Meter types configuration is not in expected format. Please check pilot configuration.');
              setMeterFuelTypes([]);
              return;
            }
          } catch (parseError) {
            console.error('Failed to parse supported_meter_types:', parseError);
            const errorMessage = parseError instanceof Error ? parseError.message : 'Unknown parsing error';
            setMeterTypesError(`Failed to parse meter types configuration: ${errorMessage}. Please check pilot configuration.`);
            setMeterFuelTypes([]);
            return;
          }
        } else {
          setMeterTypesError('Meter types configuration is empty. Please check pilot configuration.');
          setMeterFuelTypes([]);
          return;
        }
        
        if (measurementTypesConfig?.defaultValue) {
          const measurementTypes = measurementTypesConfig.defaultValue.includes(',') 
            ? measurementTypesConfig.defaultValue.split(',').map((type: string) => type.trim())
            : [measurementTypesConfig.defaultValue];
          supportedFuels = measurementTypes;
        } else {
          setMeterTypesError('Measurement types configuration is empty. Please check pilot configuration <allowed_measurement_type>');
          setMeterFuelTypes([]);
          return;
        }
        const combinations: string[] = [];
        supportedMeterTypes.forEach(meterType => {
          supportedFuels.forEach(fuel => {
            let fuelUpper = fuel.toUpperCase().trim();
            if (fuelUpper === 'ELECTRICITY') {
              fuelUpper = 'ELECTRIC';
            }
            combinations.push(`${meterType}-${fuelUpper}`);
          });
        });
        
        setMeterFuelTypes(combinations);
      } else {
        console.error('Failed to fetch ingestion configs:', response.error);
        setMeterTypesError('Failed to fetch pilot configuration. Please try again.');
        setMeterFuelTypes([]);
      }
    } catch (error) {
      console.error('Error fetching meter fuel types:', error);
      setMeterTypesError('Error fetching meter types configuration. Please try again.');
      setMeterFuelTypes([]);
    } finally {
      setLoadingMeterTypes(false);
    }
  };

  useEffect(() => {
    if (selectedPilotId) {
      fetchMeterFuelTypes();
    }
  }, [selectedPilotId]);

  // Environment configurations
  const environments = [
    { 
      id: 'Nonprod', 
      name: 'Nonprod',
      baseUrl: 'https://nonprodqaapi-external.bidgely.com',
      username: 'irfan@bidgely.com',
      password: 'bZgBogil'
    },
    { 
      id: 'NA', 
      name: 'NA',
      baseUrl: 'https://naapi.bidgely.com',
      username: 'navneetnipu@bidgely.com',
      password: 'vesVZiTj'
    },
    { 
      id: 'NA2', 
      name: 'NA2',
      baseUrl: 'https://naapi2-external.bidgely.com',
      username: 'irfan@bidgely.com',
      password: '7dKLYivX'
    },
    { 
      id: 'EU', 
      name: 'EU',
      baseUrl: 'https://euapi.bidgely.com',
      username: 'irfan@bidgely.com',
      password: 'YS6PyhLi'
    },
    { 
      id: 'CA', 
      name: 'CA',
      baseUrl: 'https://caapi.bidgely.com',
      username: 'irfan@bidgely.com',
      password: '438qHzru'
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        mb: 4,
        textAlign: 'left'
      }}>
        <Typography variant="h4" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', textAlign: 'left' }}>
          <ManageAccountsOutlinedIcon sx={{ mr: 1, color: 'primary.main', fontSize: '2rem' }} />
          Ingest Production User
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToManageUsers}
          variant="outlined"
          sx={{
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderColor: theme.palette.primary.main,
            }
          }}
        >
          Back to Manage Users
        </Button>
      </Box>
      <Box sx={{ 
          mt: 2,
          mb: 2,
          p: 2,
          bgcolor: theme.palette.info.light + '20',
          textAlign: 'left'
        }}>
          <Typography variant="body2" sx={{ 
            color: theme.palette.info.dark,
            fontWeight: 500,
            lineHeight: 1.5
          }}>
            This tool ingests the customer data in standard Bidgely format. Custom transformers are not supported. Hence, this tool should be used for new pilots before custom transformers are implemented.
          </Typography>
        </Box>

      {/* Choose Source Environment Section */}
      <Card sx={{ 
        mb: 4,
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        boxShadow: 1
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', textAlign: 'left' }}>
              <ViewStreamOutlinedIcon sx={{ mr: 1, color: 'primary.main', fontSize: '2rem' }} />
              Choose Source Environment
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'left' }}>
              Please select an environment to continue with user ingestion.
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, textAlign: 'left' }}>
              Environment
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedEnvironment}
                onChange={(e) => setSelectedEnvironment(e.target.value)}
                displayEmpty
                sx={{
                  '& .MuiSelect-icon': {
                    color: theme.palette.text.primary,
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Select an environment
                </MenuItem>
                {environments.map((env) => (
                  <MenuItem key={env.id} value={env.id}>
                    {env.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Choose Users Section */}
      <Card sx={{ 
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        boxShadow: 1
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', textAlign: 'left' }}>
              {/* <PeopleAltOutlinedIcon sx={{ mr: 1, color: 'primary.main', fontSize: '2rem' }} /> */}
              Choose Users From The Source Project
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'left' }}>
              Provide user identifiers. Data will be ingested for the last 2 years.
            </Typography>
          </Box>
          
          <Box sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, textAlign: 'left' }}>
              Enter UUID
            </Typography>
            <TextField
              fullWidth
            //   multiline
              rows={2}
              placeholder="Type a UUID and Press Enter or comma to add ..."
              value={uuids}
              onChange={(e) => handleUuidInput(e.target.value)}
              onKeyPress={handleUuidKeyPress}
              onBlur={stageUuids}
              variant="outlined"
            />
          </Box>

          {/* Meter Type Selection Section */}
          <Box sx={{ mb: 2, textAlign: 'left' }}>
            <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 500, textAlign: 'left' }}>
              Select Meter Types
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1, textAlign: 'left' }}>
              Select similar persona of users together
            </Typography>
            <FormControl component="fieldset">
              {loadingMeterTypes ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Loading meter types...
                  </Typography>
                </Box>
              ) : meterTypesError ? (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: theme.palette.error.light + '20',
                  border: `1px solid ${theme.palette.error.main}`,
                  borderRadius: 1,
                  textAlign: 'left'
                }}>
                  <Typography variant="body2" sx={{ 
                    color: theme.palette.error.main,
                    fontWeight: 500,
                    lineHeight: 1.5
                  }}>
                    {meterTypesError}
                  </Typography>
                </Box>
              ) : (
                <RadioGroup
                  value={selectedMeterTypes}
                  onChange={handleMeterTypeChange}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 2
                  }}
                >
                  {meterFuelTypes.map((meterType) => (
                    <FormControlLabel
                      key={meterType}
                      value={meterType}
                      control={
                        <Radio
                          sx={{
                            color: theme.palette.primary.main,
                            '&.Mui-checked': {
                              color: theme.palette.primary.main,
                            },
                            padding: '2px 8px 2px 0',
                          }}
                        />
                      }
                      label={meterType}
                      sx={{
                        margin: 0,
                        minHeight: 'auto',
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          padding: '2px 0',
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
              )}
            </FormControl>
          </Box>

          {stagedUuids.length > 0 && (
            <Box sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, textAlign: 'left' }}>
                Staged UUIDs ({stagedUuids.length})
              </Typography>
              <Box sx={{ 
                padding: '4px 8px',
                margin: '8px',
                border: `1px solid ${theme.palette.divider}`, 
                borderRadius: 1, 
                p: 2, 
                bgcolor: theme.palette.background.default,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1
              }}>
                {stagedUuids.map((uuid, index) => (
                  <Chip
                    key={index}
                    label={uuid}
                    onDelete={() => removeStagedUuid(uuid)}
                    size="small"
                    variant="outlined"
                    sx={{
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      backgroundColor: theme.palette.action.hover,
                      borderRadius: 3,
                      fontWeight: 'bold',
                      padding: '4px 8px',
                      margin: '8px',
                      '& .MuiChip-label': {
                        fontWeight: 'bold',
                        padding: 0,
                      },
                      '& .MuiChip-deleteIcon': {
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          color: theme.palette.text.primary,
                        }
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', width: '100%' }}>
            <BglPrimaryButton
              onClick={handleNext}
              disabled={!selectedEnvironment || stagedUuids.length === 0 || !selectedMeterTypes}
              sx={{
                width: '100%',
                backgroundColor: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  color: 'white'
                },
                '&.Mui-disabled': {
                  backgroundColor: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  opacity: 0.8,
                  color: 'white'
                }
              }}
            >
              Next: Edit Info & Set Billing
            </BglPrimaryButton>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

